// ⛳ Tipos
type PushScope = "main" | "kiosk";

interface IVapidPublicKeyResponse {
  publicKey: string;
  message?: string;
}

interface IPushSubscriptionPayload {
  scope: PushScope;
  subscription: {
    endpoint: string;
    expirationTime: number | null;
    keys: {
      p256dh: string;
      auth: string;
    };
  };
}

interface IUnsubscribePayload {
  scope: PushScope;
  endpoint: string;
}

// ─── 📌 Endpoints ─────────────────────────────────────────────────────────────
import { getEndpoints } from "@/services/apiServices/endpoints";

// ─── 📌 Servicios HTTP ────────────────────────────────────────────────────────
import { fetchGetData, fetchPostData } from "@/services/apiServices/http";

// ─── 📌 Stores ────────────────────────────────────────────────────────────────
import { useAuthStore } from "@/stores/auth";

// ⛳ Helpers internos
const urlBase64ToArrayBuffer = (base64String: string): ArrayBuffer => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);

  const bytes = Uint8Array.from(rawData, (char) => char.charCodeAt(0));

  // ✅ fuerza ArrayBuffer real, no ArrayBufferLike
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
};

const resolvePushScope = (): PushScope => {
  return window.location.pathname.startsWith("/kiosk") ? "kiosk" : "main";
};

const ensureNotificationSupport = (): void => {
  if (!("Notification" in window)) {
    throw new Error("El navegador no soporta notificaciones.");
  }

  if (!("serviceWorker" in navigator)) {
    throw new Error("El navegador no soporta Service Worker.");
  }

  if (!("PushManager" in window)) {
    throw new Error("El navegador no soporta Push API.");
  }
};

const getServiceWorkerRegistration =
  async (): Promise<ServiceWorkerRegistration | null> => {
    console.log("🔔 [push] waiting for navigator.serviceWorker.ready");
    localStorage.setItem("push:sw-step", "waiting-ready");

    try {
      const readyRegistration = (await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
      ])) as ServiceWorkerRegistration | null;

      if (readyRegistration) {
        console.log("🔔 [push] ready resolved:", readyRegistration);
        localStorage.setItem("push:sw-step", "ready-resolved");
        return readyRegistration;
      }

      const isKiosk = window.location.pathname.startsWith("/kiosk");
      const scope = isKiosk ? "/kiosk/" : "/";
      const reg = await navigator.serviceWorker.getRegistration(scope);

      if (reg) {
        console.log("🔔 [push] getRegistration resolved:", reg);
        localStorage.setItem("push:sw-step", "getRegistration-resolved");
        return reg;
      }

      localStorage.setItem("push:sw-step", "registration-null");
      return null;
    } catch {
      localStorage.setItem("push:sw-step", "registration-error");
      return null;
    }
  };

// ⛳ Composable
export const usePushNotifications = () => {
  const getAuthStore = () => useAuthStore();
  const getJwt = () => getAuthStore().getJwt;
  const getPushEndpoints = () => getEndpoints();

  const requestPermission = async (): Promise<NotificationPermission> => {
    ensureNotificationSupport();

    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";

    return Notification.requestPermission();
  };

  const getExistingSubscription =
    async (): Promise<PushSubscription | null> => {
      ensureNotificationSupport();

      const registration = await getServiceWorkerRegistration();
      if (!registration) return null;

      return registration.pushManager.getSubscription();
    };

  const getVapidPublicKey = async (): Promise<string> => {
    const endpoints = getPushEndpoints();

    const response = await fetchGetData(endpoints.pushVapidPublicKey, getJwt());

    if (!response) {
      throw new Error("No se pudo obtener la VAPID public key.");
    }

    const data = response as IVapidPublicKeyResponse;

    if (!data?.publicKey) {
      throw new Error(data?.message || "La respuesta no contiene publicKey.");
    }

    return data.publicKey;
  };

  const saveSubscriptionInBackend = async (
    subscription: PushSubscription,
    scope: PushScope,
  ): Promise<void> => {
    const endpoints = getPushEndpoints();
    const json = subscription.toJSON();

    const payload: IPushSubscriptionPayload = {
      scope,
      subscription: {
        endpoint: json.endpoint ?? "",
        expirationTime: json.expirationTime ?? null,
        keys: {
          p256dh: json.keys?.p256dh ?? "",
          auth: json.keys?.auth ?? "",
        },
      },
    };

    const response = await fetchPostData(
      endpoints.pushSubscribe,
      getJwt(),
      payload,
    );

    console.log("🔔 [push] backend subscribe raw response:", response);
  };

  const subscribe = async (): Promise<PushSubscription> => {
    ensureNotificationSupport();

    try {
      console.log("🔔 [push] subscribe:start");
      localStorage.setItem("push:subscribe-step", "start");

      const permission = await requestPermission();
      console.log("🔔 [push] permission:", permission);
      localStorage.setItem("push:subscribe-step", `permission:${permission}`);

      if (permission !== "granted") {
        throw new Error("Permiso de notificaciones no concedido.");
      }

      const registration = await getServiceWorkerRegistration();
      if (!registration) {
        throw new Error("Service Worker no disponible.");
      }
      console.log("🔔 [push] registration:", registration);
      localStorage.setItem("push:subscribe-step", "registration-ready");

      const scope = resolvePushScope();
      console.log("🔔 [push] scope:", scope);
      localStorage.setItem("push:subscribe-step", `scope:${scope}`);

      let subscription = await registration.pushManager.getSubscription();
      console.log("🔔 [push] existing subscription:", subscription);
      localStorage.setItem(
        "push:subscribe-step",
        subscription ? "existing-subscription" : "no-subscription",
      );

      if (!subscription) {
        const publicKey = await getVapidPublicKey();
        console.log("🔔 [push] vapid key received");
        localStorage.setItem("push:subscribe-step", "vapid-key-ok");

        const applicationServerKey = urlBase64ToArrayBuffer(
          publicKey,
        ) as BufferSource;

        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });

        console.log("🔔 [push] new subscription created:", subscription);
        localStorage.setItem("push:subscribe-step", "subscription-created");
      }

      await saveSubscriptionInBackend(subscription, scope);
      console.log("🔔 [push] backend subscribe ok");
      localStorage.setItem("push:subscribe-step", "backend-saved");

      return subscription;
    } catch (error: any) {
      const msg =
        error instanceof Error ? error.message : JSON.stringify(error ?? "");
      console.error("❌ [push] subscribe failed:", error);
      localStorage.setItem("push:subscribe-step", "error");
      localStorage.setItem("push:subscribe-error", msg);
      throw error;
    }
  };

  const unsubscribe = async (): Promise<boolean> => {
    ensureNotificationSupport();

    const registration = await getServiceWorkerRegistration();
    if (!registration) {
      console.warn("No hay Service Worker disponible para desuscribir push.");
      return false;
    }

    const scope = resolvePushScope();
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return false;
    }

    const endpoints = getPushEndpoints();

    const payload: IUnsubscribePayload = {
      scope,
      endpoint: subscription.endpoint,
    };

    await fetchPostData(endpoints.pushUnsubscribe, getJwt(), payload);

    await subscription.unsubscribe();
    return true;
  };

  const isPermissionGranted = (): boolean => {
    return "Notification" in window && Notification.permission === "granted";
  };

  const isSupported = (): boolean => {
    return (
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window
    );
  };

  return {
    requestPermission,
    getExistingSubscription,
    subscribe,
    unsubscribe,
    isPermissionGranted,
    isSupported,
    resolvePushScope,
  };
};
